import {useCallback, useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {toast} from 'react-toastify';

import {merchantService} from '@/services/merchants';
import type {AfriexBusinessUser} from '@/types/AdminBusinessUser';
import type {AfriexSecurityNote} from '@/types/Notes';
import {getErrorMessage} from '@/utils/errorMessage';
import {logRejection} from '@/utils/logRejection';

const SECURITY_NOTES_LIMIT = 6;

export default function useMerchantUserDetails() {
  const params = useParams();
  const userId = params?.userId || '';
  const [loading, setIsLoading] = useState(false);
  const [personDetails, setPersonDetails] = useState<AfriexBusinessUser>(
    {} as AfriexBusinessUser,
  );
  const [securityNotes, setSecurityNotes] = useState<AfriexSecurityNote[]>([]);
  const [isLoadingSecurityNotes, setIsLoadingSecurityNotes] = useState(false);
  const [securityNotesPage, setSecurityNotesPage] = useState(0);
  const [hasMoreNotes, setHasMoreNotes] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);

  const fetchBusinessUser = useCallback(async (): Promise<void> => {
    try {
      if (!userId) {
        return;
      }
      setIsLoading(true);
      const response = await merchantService.getBusinessUser(userId);
      if (response?.person) {
        setPersonDetails(response.person);
      }
    } catch (err) {
      const errorMessage = getErrorMessage(err, '');
      toast.error('Failed to fetch business user details:' + errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  const fetchSecurityNotes = useCallback(
    async (params: {page: number}): Promise<void> => {
      if (!userId) {
        return;
      }
      const isFirstPage = params.page === 0;
      if (isFirstPage) {
        setIsLoadingSecurityNotes(true);
      } else {
        setIsFetchingMore(true);
      }
      try {
        const result = await merchantService.getBusinessSecurityNotes(userId, {
          page: params.page,
          limit: SECURITY_NOTES_LIMIT,
        });
        setSecurityNotes((prev) =>
          isFirstPage ? result.data : [...prev, ...result.data],
        );
        setHasMoreNotes(result.data.length >= SECURITY_NOTES_LIMIT);
      } catch (err) {
        toast.error(getErrorMessage(err, 'Failed to fetch security notes'));
      } finally {
        if (isFirstPage) {
          setIsLoadingSecurityNotes(false);
        } else {
          setIsFetchingMore(false);
        }
      }
    },
    [userId],
  );

  const loadMoreSecurityNotes = () => setSecurityNotesPage((p) => p + 1);

  useEffect(() => {
    // Both fetchers toast their own failures, so these only fire if that catch
    // or the finally itself throws. Log rather than swallow silently.
    fetchBusinessUser().catch(
      logRejection('Failed to fetch business user details'),
    );
  }, [fetchBusinessUser, userId]);

  useEffect(() => {
    fetchSecurityNotes({page: securityNotesPage}).catch(
      logRejection('Failed to fetch business user security notes'),
    );
  }, [securityNotesPage, fetchSecurityNotes]);

  return {
    loading,
    user: personDetails,
    securityNotes,
    isLoadingSecurityNotes,
    hasMoreNotes,
    loadMoreSecurityNotes,
    isFetchingMore,
  };
}
